/**
 * The top-bar account menu: an avatar button that opens a dropdown with the
 * signed-in user's name + role and the Profile / Log out actions. Self-contained
 * like {@link NotificationBell} — it reads the principal and `signOut` straight
 * from the session and owns its open/close + keyboard-dismiss state — so the
 * shell mounts `<ProfileMenu />` with no wiring. Always present in the top bar,
 * for every role.
 *
 * The button shows the user's uploaded picture when there is one, falling back
 * to their initials and then to a neutral glyph. The image comes from
 * {@link useAvatar}, which fetches nothing unless the principal says an avatar
 * exists — so for most users this costs no request at all.
 */
import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { roleLabel } from '../auth/roles';
import { useSession } from '../auth/session';
import { useAvatar } from '../auth/useAvatar';
import { useOnClickOutside, useOnEscape } from '../lib/hooks';
import { initialsFor } from '../lib/initials';

import { UserIcon } from './icons';

export interface ProfileMenuProps {
  /** Override navigation (defaults to react-router), matching NotificationBell. */
  onNavigate?: (path: string) => void;
}

export function ProfileMenu({ onNavigate }: ProfileMenuProps): React.JSX.Element {
  const { principal, signOut } = useSession();
  const { url: avatarUrl } = useAvatar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(rootRef, open, () => { setOpen(false); });
  useOnEscape(open, () => { setOpen(false); });

  const displayName = principal?.display_name ?? null;
  const email = principal?.email ?? null;
  const role = principal?.role ?? null;
  const initials = initialsFor(displayName);

  const go = (path: string): void => {
    setOpen(false);
    if (onNavigate) onNavigate(path);
    else void navigate(path);
  };

  const onLogOut = (): void => {
    signOut();
    go('/login');
  };

  const buttonLabel = displayName === null ? 'Account menu' : `Account menu for ${displayName}`;

  return (
    <div className="menu-anchor" ref={rootRef}>
      <button
        type="button"
        className={
          initials === null && avatarUrl === null
            ? 'avatar avatar-button avatar-empty'
            : 'avatar avatar-button'
        }
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={buttonLabel}
        onClick={() => { setOpen((o) => !o); }}
      >
        {avatarUrl !== null ? (
          // Decorative: the button's aria-label already names the account.
          <img className="avatar-image" src={avatarUrl} alt="" />
        ) : initials === null ? (
          <UserIcon />
        ) : (
          <span aria-hidden="true">{initials}</span>
        )}
      </button>
      {open ? (
        <div className="menu profile-menu" id={menuId} role="menu" aria-label="Account">
          <div className="profile-menu-header">
            <p className="profile-menu-name">{displayName ?? 'Signed in'}</p>
            {role !== null ? <p className="profile-menu-role">{roleLabel(role)}</p> : null}
            {email !== null ? <p className="profile-menu-email">{email}</p> : null}
          </div>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => { go('/profile'); }}
          >
            Profile
          </button>
          <button type="button" role="menuitem" className="menu-item" onClick={onLogOut}>
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
